import React from "react";
import { Chart, ChartOptions, ChartPoint, TimeUnit } from "chart.js";
import moment from "moment-timezone";
import { ETrendTimeScope, IGroupedTrend, ITrendSettingsDocument } from "../../../models/data/dashboard/Trends/TrendsData";
import * as utils from "../../../helpers/utils";
import 'chartjs-plugin-annotation';
import { IDate } from "../../../models";

// Define the CustomChartDataSets interface
interface CustomChartDataSets extends Chart.ChartDataSets {
    numerators: number[];
    denominators: number[];
    isSummation: boolean;
}

export interface IProps {
    groupedTrends: IGroupedTrend[];
    scope: ETrendTimeScope;
    startDate: moment.Moment; // Start date as a moment object
    endDate: moment.Moment;   // End date as a moment object
    settings: ITrendSettingsDocument;
    value: number;
    updateDateRange(startDate: IDate, endDate: IDate): void; 
}

export default class TrendLine extends React.Component<IProps, any> {
    trend_line_chart_details: Chart;
    colors = ["green", "blue", "orange", "purple", "red"]; // More colors if needed
    ctx: CanvasRenderingContext2D | null = null;

    constructor(props) {
        super(props);
        this.state = {
            noData: false
        };
    }

    componentDidMount() {
        const ctx: HTMLCanvasElement = document.getElementById("trend_line_chart_details") as HTMLCanvasElement;
        this.ctx = ctx.getContext("2d"); // Initialize ctx
        this.loadChart();
    }

    componentDidUpdate(prevProps: IProps) {
        if (
            prevProps.groupedTrends !== this.props.groupedTrends ||
            prevProps.scope !== this.props.scope ||
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate
        ) {
            this.loadChart();
        }
    }

    getScopeTimeString(): TimeUnit {
        switch (this.props.scope) {
            case ETrendTimeScope.Daily:
                return "day";
            case ETrendTimeScope.Monthly:
                return "month";
            case ETrendTimeScope.Weekly:
                return "week";
            case ETrendTimeScope.Quarterly:
                return "quarter";
            case ETrendTimeScope.SemiAnnualy:
                return "year";
            case ETrendTimeScope.Annualy:
                return "year";
            default:
                return "week";
        }
    }

    calculateTickSize(yMin: number, yMax: number): number {
        const range = yMax - yMin;
        if (range <= 10) {
            return 1; // If the range is small, use a tick size of 1
        } else if (range <= 50) {
            return 5; // For a moderate range, use a tick size of 5
        } else {
            return 10; // For larger ranges, use a tick size of 10
        }
    }

    loadChart() {
        // Find the first groupedTrend with SeriesData
        const firstValidTrend = this.props.groupedTrends.find(trend => trend.SeriesData && trend.SeriesData.length > 0);

        if (!firstValidTrend) {
            this.setState({ noData: true });
            if (this.trend_line_chart_details) {
                this.trend_line_chart_details.destroy();
            }
            return;
        }
    
        this.setState({ noData: false });

        // Dynamic y-axis scaling
        const allValues = this.props.groupedTrends.reduce((acc, trend) => [...acc, ...trend.SeriesData.map(item => item.Value)], []);
        let yMin = Math.min(...allValues);
        let yMax = Math.max(...allValues);
    
        // Calculate tick size based on the scope (e.g., weekly, monthly)
        const tickSize = this.calculateTickSize(yMin, yMax);
    
        // Round min and max to the nearest tick
        yMin = Math.floor(yMin / tickSize) * tickSize;
        yMax = Math.ceil(yMax / tickSize) * tickSize;
    
        if (this.trend_line_chart_details) {
            this.trend_line_chart_details.destroy();
        }

        const units = firstValidTrend.Settings.UnitName;
        const dateFormat = "YYYY-MM-DD"; // Use ISO format for consistency
        const referenceTimezone = moment.tz.guess();
        const chart_data = {
            labels: firstValidTrend.SeriesData.map((item) => moment.utc(item.DateTime).format(dateFormat)),
            datasets: this.props.groupedTrends.map((trend, index) => {
                const numerators = new Array(trend.SeriesData.length).fill(0);
                const denominators = new Array(trend.SeriesData.length).fill(0);
        
                trend.SeriesData.forEach((item, idx) => {
                    numerators[idx] = item.Numerator || 0;
                    denominators[idx] = item.Denominator || 0;
                });
                
                return {
                    label: trend.Settings.Name,
                    backgroundColor: utils.getTrendColorGradient(this.colors[index], 1, this.ctx),
                    borderColor: utils.getTrendColor(this.colors[index] || "green"),
                    pointBorderWidth: 0,
                    data: trend.SeriesData.map((item) => ({
                        y: item.Value,
                        x: moment.utc(item.DateTime).format(dateFormat),
                        date: moment.utc(item.DateTime).format(dateFormat)
                    })),
                    tension: 0,
                    isSummation: trend.Settings.AggregationSettings.AggregationType === 0,
                    numerators: numerators,
                    denominators: denominators
                };
            })
        };
    
        const axisList = [{
            id: "y_axis",
            position: "left",
            ticks: {
                autoSkip: true,
                padding: 10,
                autoSkippingPadding: 25,
                precision: 0,
                fontColor: "#000000",
                callback: function (value) {
                    return value + " " + units;
                },
                min: yMin,
                max: yMax,
            },
            gridLines: {
                display: true
            },
            display: true,
        }];
    
        const chart_options: ChartOptions & { annotation: any } = {
            responsive: true,
            elements: {
                point: {
                    radius: 3
                }
            },
            annotation: {
                annotations: [
                    {
                        type: 'line',
                        mode: 'horizontal',
                        scaleID: 'y-axis-0',
                        value: 80,
                        borderColor: '#FF0000',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        label: {
                            content: 'Upper Bound',
                            enabled: true,
                            position: 'right',
                        },
                    },
                    {
                        type: 'line',
                        mode: 'horizontal',
                        scaleID: 'y-axis-0',
                        value: 20,
                        borderColor: '#0000FF',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        label: {
                            content: 'Lower Bound',
                            enabled: true,
                            position: 'right',
                        },
                    },
                ],
            },
            scales: {
                scaleLabel: {
                    fontColor: "#000000",
                },
                yAxes: axisList,
                xAxes: [
                    {
                        display: true,
                        gridLines: {
                            display: true
                        },
                        ticks: {
                            minRotation: 45,
                        },
                        type: "time",
                        time: {
                            unit: this.getScopeTimeString(),
                            min: moment(this.props.startDate).format(dateFormat),
                            max: moment(this.props.endDate).format(dateFormat),
                        }
                    }
                ]
            },
            legend: {
                display: true,
                position: "top"
            },
            tooltips: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset = data.datasets[tooltipItem.datasetIndex] as CustomChartDataSets;
                        const index = tooltipItem.index;
                        const numerator = dataset.numerators[index];
                        const denominator = dataset.denominators[index];
                        const isSummation = dataset.isSummation;
                        if (isSummation) {
                            return `${dataset.label}: ${tooltipItem.yLabel}`;
                        } else {
                            return `${dataset.label}: ${tooltipItem.yLabel}% (${numerator}/${denominator})`;
                        }
                    }
                }
            },
            maintainAspectRatio: true,
            aspectRatio: (3 / 1),
            onClick: (event, elements) => {
                if (elements && elements.length > 0) {
                    const clickedPoint = elements[0] as any;
                    const index = clickedPoint._index;
                    let date: ChartPoint | undefined;

                    for (let i = 0; i < this.trend_line_chart_details.data.datasets.length; i++) {
                        const dataset = this.trend_line_chart_details.data.datasets[i];
                        if (dataset.data[index]) {
                            date = dataset.data[index] as ChartPoint;
                            break;
                        }
                    }
                    const clickedDate = moment.tz(date.x, referenceTimezone);
                    console.log("Clicked date:", clickedDate);
                    let newStartDate = clickedDate.clone();
                    let newEndDate = clickedDate.clone();
    
                    switch (this.props.scope) {
                        case ETrendTimeScope.Daily:
                            newStartDate.startOf('day');
                            newEndDate.endOf('day');
                            break;
                        case ETrendTimeScope.Weekly:
                            newStartDate.startOf('week');
                            newEndDate.endOf('week');
                            break;
                        case ETrendTimeScope.Monthly:
                            newStartDate.startOf('month');
                            newEndDate.endOf('month');
                            break;
                        case ETrendTimeScope.Quarterly:
                            newStartDate.startOf('quarter');
                            newEndDate.endOf('quarter');
                            break;
                        case ETrendTimeScope.SemiAnnualy:
                            const currentMonth = clickedDate.month();
                            const startMonth = currentMonth < 6 ? 0 : 6;
                            newStartDate.month(startMonth).startOf('month');
                            newEndDate.month(startMonth + 5).endOf('month');
                            break;
                        case ETrendTimeScope.Annualy:
                            newStartDate.startOf('year');
                            newEndDate.endOf('year');
                            break;
                    }
    
                    this.props.updateDateRange(newStartDate.toDate(), newEndDate.toDate());
                }
            }
        };
    
        this.trend_line_chart_details = new Chart(this.ctx, {
            data: chart_data,
            options: chart_options,
            type: "line",
        });
    }

    createGradient(ctx: CanvasRenderingContext2D, gradient1: string, gradient2: string) {
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, gradient1);
        gradient.addColorStop(1, gradient2);
        return gradient;
    }

    render() {
        return (
            <div style={{ position: 'relative', paddingTop: 5, paddingBottom: 5 }}>
                {this.state.noData && (
                    <div style={{
                        position: 'relative',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, 0%)',
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)'
                    }}>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>No Data Found for Current Date and Scope</p>
                    </div>
                )}
                <canvas id="trend_line_chart_details" style={{ display: 'block' }} />
            </div>
        );
    }
}
